import { useEffect, useState } from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { CONSULTATION_URL } from "@/data/constants";
import { ArrowRight, Download, ShieldCheck, FileText, Calendar } from "lucide-react";

const TrustCertifications = () => {
  const [badges, setBadges] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    api.get("/compliance-badges").then((r) => setBadges(r.data.badges || [])).catch(() => {});
    api.get("/trust-documents").then((r) => setDocuments(r.data.documents || [])).catch(() => {});
  }, []);

  const badgeLabel = (slug) => badges.find((b) => b.slug === slug)?.label || slug;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const isExpired = (d) => d.expiration_date && new Date(d.expiration_date) < new Date();

  return (
    <PublicLayout>
      <section className="ft-section-dark py-24" data-testid="trust-hero">
        <div className="ft-container ft-fade-in max-w-3xl relative">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">
            Trust &amp; Certifications
          </div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight mb-6">
            The quality standards behind<br />
            <span className="ft-serif text-[var(--ft-gold)]">every FLOWTALIS bottle</span>.
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            FLOWTALIS partners with carefully selected manufacturing facilities that meet rigorous quality, safety,
            and regulatory standards. The certifications and compliance programs below represent manufacturing
            capabilities available through our production partners. Supporting documentation can be provided upon
            request for qualifying projects.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="ft-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="trust-grid">
            {badges.map((b) => (
              <div key={b.slug} className="ft-card group" data-testid={`trust-card-${b.slug}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center border border-[var(--ft-gold)]" style={{ background: "rgba(200,162,74,0.08)" }}>
                    <ShieldCheck size={18} className="text-[var(--ft-gold)]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-bold text-[var(--ft-black)] leading-tight">{b.label}</div>
                    <div className="text-[0.65rem] font-mono text-[var(--ft-muted)]">{b.slug}</div>
                  </div>
                </div>
                <p className="text-sm text-[var(--ft-muted)] leading-relaxed">
                  {b.description || <span className="italic">Certification details available on request.</span>}
                </p>
                {b.document_url && (
                  <a
                    href={b.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[var(--ft-gold)] hover:gap-3 transition-all"
                    data-testid={`trust-doc-${b.slug}`}
                  >
                    <Download size={14} /> Download documentation
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {documents.length > 0 && (
        <section className="py-20 bg-[var(--ft-light-gray)] border-t border-[var(--ft-border)]" data-testid="trust-docs-section">
          <div className="ft-container">
            <div className="max-w-2xl mb-12">
              <div className="ft-eyebrow mb-4">Documentation Library</div>
              <span className="ft-gold-rule-lg" />
              <h2 className="mt-6 text-[clamp(1.875rem,3.5vw,2.75rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
                Verified <span className="ft-serif font-medium text-[var(--ft-gold)]">documentation</span> at your fingertips.
              </h2>
              <p className="mt-5 text-[var(--ft-muted)] text-lg leading-relaxed">
                COAs, facility certifications, spec sheets and lab reports — published and current.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="trust-docs-grid">
              {documents.filter((d) => !isExpired(d)).map((d) => {
                const href = d.upload_id ? `/api/uploads/${d.upload_id}` : d.file_url;
                return (
                  <div key={d.id} className="ft-card group" data-testid={`trust-doc-card-${d.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="h-10 w-10 rounded-full flex items-center justify-center border border-[var(--ft-gold)] shrink-0" style={{ background: "rgba(200,162,74,0.08)" }}>
                        <FileText size={16} className="text-[var(--ft-gold)]" strokeWidth={1.5} />
                      </div>
                      <span className="text-[0.6rem] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-[var(--ft-border)] text-[var(--ft-charcoal)]">{d.doc_type}</span>
                    </div>
                    <h3 className="font-bold text-[var(--ft-black)] leading-tight">{d.title}</h3>
                    {d.description && <p className="mt-2 text-sm text-[var(--ft-muted)] leading-relaxed line-clamp-3">{d.description}</p>}
                    {(d.effective_date || d.expiration_date) && (
                      <div className="mt-3 flex items-center gap-3 text-[0.7rem] text-[var(--ft-muted)]">
                        {d.effective_date && (<span className="inline-flex items-center gap-1"><Calendar size={10}/> Effective {fmtDate(d.effective_date)}</span>)}
                        {d.expiration_date && (<span className="inline-flex items-center gap-1">· Expires {fmtDate(d.expiration_date)}</span>)}
                      </div>
                    )}
                    {(d.badge_slugs?.length > 0 || d.product_slugs?.length > 0) && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {(d.badge_slugs || []).map((s) => (
                          <span key={`b-${s}`} className="text-[0.6rem] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border border-[var(--ft-gold)] text-[var(--ft-gold)]">{badgeLabel(s)}</span>
                        ))}
                        {(d.product_slugs || []).map((s) => (
                          <span key={`p-${s}`} className="text-[0.6rem] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border border-[var(--ft-border)] text-[var(--ft-charcoal)]">{s.replace(/-/g, " ")}</span>
                        ))}
                      </div>
                    )}
                    {href && (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[var(--ft-gold)] hover:gap-3 transition-all" data-testid={`trust-doc-download-${d.id}`}>
                        <Download size={14} /> Download
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-24 bg-[var(--ft-light-gray)] border-t border-[var(--ft-border)]">
        <div className="ft-container text-center max-w-3xl">
          <div className="ft-eyebrow mb-5">Documentation Requests</div>
          <span className="ft-gold-rule-lg mx-auto" />
          <h2 className="mt-6 text-[clamp(1.875rem,3.5vw,2.75rem)] font-bold tracking-tight text-[var(--ft-black)] mb-6">
            Need a specific <span className="ft-serif font-medium text-[var(--ft-gold)]">certification file</span>?
          </h2>
          <p className="text-[var(--ft-muted)] mb-8 leading-relaxed">
            Book a consultation and we&apos;ll route the exact COA, GMP audit, or facility certification
            you need directly to your compliance team.
          </p>
          <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold" data-testid="trust-book">
            Book a Consultation <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* V1.2.4 — Manufacturing & Certification Notice. Subtle but visible;
          typography intentionally muted so it reads as legal notice rather
          than marketing copy. */}
      <section className="py-14 bg-white border-t border-[var(--ft-border)]" data-testid="trust-disclaimer">
        <div className="ft-container max-w-4xl">
          <div className="ft-eyebrow mb-3 text-[var(--ft-muted)]">Manufacturing &amp; Certification Notice</div>
          <p className="text-xs md:text-sm text-[var(--ft-muted)] leading-relaxed italic">
            Certification availability may vary depending on the manufacturing partner, production facility,
            beverage type, formulation, and packaging selected. Documentation for applicable certifications and
            compliance programs can be provided upon request for qualifying projects.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
};

export default TrustCertifications;
